import { toWirePath } from './brand-wire';

export const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN as string) ?? 'https://api.adholics.net';

export class ApiError extends Error {
  status: number;
  /**
   * The `error` field of the response: a COARSE class, not the reason.
   *
   * Most of the API's refusals are built by helpers that put a generic
   * envelope code here -- `conflict`, `not_found`, `bad_request` -- and the
   * specific code in `message`. `code` is deliberately left as the envelope,
   * because several call sites branch on it (`code === 'bad_request'`,
   * `code === 'forbidden'`) and quietly changing what it means would change
   * their behaviour. Read `specificCode` when you want the reason.
   */
  code: string;
  /** Any extra fields the refusal carried. See `humanizeError` before using. */
  details: Record<string, unknown> | null;
  constructor(
    status: number,
    code: string,
    message?: string,
    details?: Record<string, unknown> | null,
  ) {
    super(message ?? code);
    this.status = status;
    this.code = code;
    this.details = details ?? null;
  }

  /**
   * The reason, when the envelope was hiding one.
   *
   * `err()` sends `{ error: <envelope>, message: <specific code> }`, so the
   * useful string is in `message` -- but `message` is ALSO where prose lands
   * (`err(502, 'ai_run_failed', someError.message)` and a 500-character slice
   * of a Stripe error body both go there). Only a value shaped like one of our
   * codes is treated as one; anything with whitespace or capitals is prose and
   * is left alone, so it can never be looked up or shown as a code.
   */
  get specificCode(): string | null {
    const m = this.message;
    if (!m || m === this.code) return null;
    return CODE_SHAPE.test(m) ? m : null;
  }
}

/**
 * `slug_in_use`, `duplicate.brands.owner_org_id+slug`,
 * `quota_exceeded:data_sources:25/25` -- lowercase, no whitespace, with dotted
 * segments and colon-separated parameters.
 */
const CODE_SHAPE = /^[a-z][a-z0-9_]*(?:\.[a-z0-9_+]+)*(?::[^\s]+)*$/;

/**
 * Build an ApiError from a raw response, for the fetches that cannot use api().
 *
 * A signed-out page (the public embed) and a few multipart uploads hand-roll
 * their fetch, and every one of them read `parsed.error` — the envelope — and
 * threw the reason away. On the embed page that made all three of its written
 * sentences unreachable: apps/api/src/embed/api.ts refuses with notFound() four
 * times over, so `error` is always `not_found` and no branch comparing it to
 * `token_revoked` could ever fire.
 *
 * This is the same contract `refusalCode()` implements on the worker side, and
 * it exists so that the reading of a refusal happens in ONE place on each side
 * of the wire rather than being re-derived at every call site — which is how
 * three of them derived it wrongly.
 */
export function apiErrorFrom(status: number, bodyText: string): ApiError {
  let parsed: Record<string, unknown> | null = null;
  try {
    const value: unknown = JSON.parse(bodyText);
    if (value && typeof value === 'object') parsed = value as Record<string, unknown>;
  } catch {
    // Not JSON — a gateway page, an empty body. `http_error` has a sentence.
  }
  const envelope =
    typeof parsed?.error === 'string' && parsed.error.length > 0 ? parsed.error : 'http_error';
  const message = typeof parsed?.message === 'string' ? parsed.message : envelope;
  return new ApiError(status, envelope, message, parsed);
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  accessToken?: string | null;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json',
  };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.accessToken) headers.authorization = `Bearer ${opts.accessToken}`;

  const res = await fetch(`${API_ORIGIN}${toWirePath(path)}`, {
    method: opts.method ?? (opts.body ? 'POST' : 'GET'),
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'include',
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const body = data as Record<string, unknown> | null;
    throw new ApiError(
      res.status,
      (body?.error as string) ?? 'http_error',
      body?.message as string | undefined,
      body,
    );
  }
  return data as T;
}

/**
 * Fetch a binary attachment (currently: report PDFs).
 *
 * Separate from `api()` because that one parses the body as JSON, which would
 * corrupt a PDF. Everything else is deliberately identical — same origin, same
 * bearer header, same credentials, and above all the SAME error mapping via
 * `apiErrorFrom`, so a refusal here reaches `humanizeError` in exactly the shape
 * it does everywhere else. A second hand-rolled error path is how the reason gets
 * thrown away and the user is shown an envelope.
 */
export async function apiBlob(
  path: string,
  opts: { accessToken?: string | null; signal?: AbortSignal } = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const headers: Record<string, string> = {};
  if (opts.accessToken) headers.authorization = `Bearer ${opts.accessToken}`;

  const res = await fetch(`${API_ORIGIN}${toWirePath(path)}`, {
    headers,
    credentials: 'include',
    signal: opts.signal,
  });

  if (!res.ok) throw apiErrorFrom(res.status, await res.text());

  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await res.blob(), filename: match ? (match[1] as string) : null };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
